import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { resolveResultHl7Document } from "features/results/utils/resultHl7Document";

/**
 * A result's HL7 document, fetched when the row is opened (KIN-2120).
 *
 * The same endpoint and shape a lab order's documents come from
 * (features/labOrders/hooks/useLabOrderXml): keyed on the message control id, with a
 * 404 — no stored document — read as nothing to show rather than a failure.
 *
 * Opening a result is audited on the backend (KIN-2097), which the list never was. One
 * fetch per opened row; re-opening the same row is served from the cache.
 */
export const useResultHl7Document = (item) => {
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const { messageControlId, shouldFetch } = resolveResultHl7Document({ item });

  const { data, isFetching } = useQuery({
    queryKey: ["resultMessageXml", messageControlId, tenantVal],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();

      try {
        const response = await axiosInstance.get(
          `${tenantVal}/healthlink/messages/${messageControlId}/xml`
        );

        return response?.data ?? null;
      } catch (error) {
        if (error?.response?.status === 404) return null;

        throw error;
      }
    },
    enabled: shouldFetch && !!tenantVal,
    staleTime: 1000 * 60,
    meta: { skipGlobalError: true },
  });

  return resolveResultHl7Document({ item, fetched: data, isFetching });
};
